/**
 * The js-data plugin for Entity Data Model
 * @module component/edm-js-data/index
 */
import _ from 'underscore';
import JSData from 'js-data';
import Schemator from 'js-data-schema';
import {
  defineConstProperty,
  defineProducedPropertyOnClass,
  defineProducedProperty,
} from '@bingads-webui/reflection';
import { defineResource } from './js-data';

/**
 * @param {EDM} edm - The EDM to apply the plugin to
 * @param {ResourceIdentifier} root - The root resource identifier
 * @return {void} Nothing to return
 */
export function jsdataPlugin(edm, root) {
  if (edm.jsdata) {
    return;
  }

  if (!edm.schema) {
    throw new Error('Schemas must be loaded before applying the js-data plugin');
  }

  const store = new JSData.DS({ cacheResponse: false });
  const schemator = new Schemator();

  /**
   * @name edm
   * @property {Object} jsdata - An aggregator of js-data objects
   * @property {JSDataDS} jsdata.store - The js-data data store
   * @property {JSDataResource[]} jsdata.resources - An array of all js-data resources created
   * @property {JSDataSchemator} jsdata.schemator - The js-data schemator
   */
  defineConstProperty(edm, 'jsdata', (() => {
    const resources = {};

    edm.types.each((type) => {
      if (type.schema) {
        const { name } = type.schema;

        defineProducedProperty(resources, name, () => defineResource({
          schema: type.schema,
          store,
          schemator,
          edm,
          edmRootResourceIdentifier: root,
        }));

        /**
         * @name type
         * @type Type
         * @property {JSDataResource} jsdataResource - The js-data resource for the type
         */
        defineProducedProperty(type, 'jsdataResource', () => resources[name]);
      }
    });

    return _.chain({})
      .defineConstProperty('store', store)
      .defineConstProperty('resources', resources)
      .defineConstProperty('schemator', schemator)
      .value();
  })());

  /**
   * @memberof ResourceIdentifier#
   * @this ResourceIdentifier
   * @returns {JSDataResource} The js-data resource for the entity
   */
  function jsdataResourceFactory() {
    if (_.has(store.definitions, this.path)) {
      return store.definitions[this.path];
    }

    if (this.type instanceof edm.types.CollectionType &&
      // action has same path for different parameters
      !(this.navigation.source.type instanceof edm.types.ActionType)) {
      return defineResource({
        schema: this.type.elementType.schema,
        store,
        schemator,
        name: this.path,
        entitySet: this,
        edm,
        edmRootResourceIdentifier: root,
      });
    }

    throw new Error('only support jsdataResource on collection type edm');
  }

  if (edm.resourceIdentifiers) {
    const {
      ResourceIdentifier,
    } = edm.resourceIdentifiers;

    defineProducedPropertyOnClass(ResourceIdentifier, 'jsdataResource', jsdataResourceFactory);
  }
}
